// Goal Pace — pure trend math + healthy-rate guardrails. No React, no I/O.
//
// Philosophy baked in: weekly trends over daily noise, sustainable-rate
// guardrails, and "faster is NOT better". A single up-week (water/glycogen)
// must never trigger alarming copy — only multi-week trends do.
//
// All weights are kilograms. Callers convert for display via useUnits.

export const PROTEIN_TARGET_G = 175
export const STEP_TARGET_MIN = 10000
export const STEP_TARGET_MAX = 12000
// Healthy weekly LOSS band as a fraction of current bodyweight.
export const BAND_MIN_FRAC = 0.005 // 0.5%/wk
export const BAND_MAX_FRAC = 0.010 // 1.0%/wk
export const TOO_FAST_FRAC = 0.012 // >1.2%/wk sustained = caution
export const MIN_WEIGHINS_FOR_BASELINE = 7

// --- date helpers (YYYY-MM-DD, local) --------------------------------------
export function parseKey(key) {
  return key ? new Date(`${key}T00:00:00`) : null
}
export function keyOf(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
export function todayKey(now = new Date()) {
  return keyOf(now)
}
export function daysBetween(aKey, bKey) {
  const a = parseKey(aKey)
  const b = parseKey(bKey)
  if (!a || !b) return null
  return Math.round((b.getTime() - a.getTime()) / 86400000)
}
export function addDaysKey(key, n) {
  const d = parseKey(key)
  if (!d) return null
  d.setDate(d.getDate() + n)
  return keyOf(d)
}

// --- weekly weight series --------------------------------------------------
// entries: ascending [{ date, weight }]. Buckets are trailing 7-day windows
// ending on `anchorKey` (today): bucket for weekEnd W covers (W-6 .. W).
// Returns chronological [{ weekEndKey, avg, count }] from the earliest week
// containing data up to the anchor week. This is the "7-day rolling average,
// sampled weekly" used for both the headline number and the chart.
export function weeklyWeightSeries(entries, anchorKey) {
  const clean = (entries || [])
    .filter((e) => e && e.date && e.weight != null && Number.isFinite(Number(e.weight)))
    .map((e) => ({ date: e.date, weight: Number(e.weight) }))
    .sort((a, b) => (a.date < b.date ? -1 : 1))
  if (clean.length === 0) return []

  const anchor = anchorKey || clean[clean.length - 1].date
  const earliest = clean[0].date
  const out = []
  // Walk weeks backward from the anchor until we pass the earliest reading.
  let weekEnd = anchor
  // guard against runaway loops (max ~3 years of weeks)
  for (let i = 0; i < 160; i++) {
    const winStart = addDaysKey(weekEnd, -6)
    const inWin = clean.filter((e) => e.date >= winStart && e.date <= weekEnd)
    if (inWin.length) {
      const avg = inWin.reduce((s, e) => s + e.weight, 0) / inWin.length
      out.push({ weekEndKey: weekEnd, avg, count: inWin.length })
    }
    if (winStart <= earliest) break
    weekEnd = addDaysKey(weekEnd, -7)
  }
  return out.reverse() // chronological
}

// Last raw reading on/at the anchor day (or the latest reading overall).
export function latestRaw(entries) {
  const clean = (entries || [])
    .filter((e) => e && e.date && e.weight != null)
    .sort((a, b) => (a.date < b.date ? -1 : 1))
  const last = clean[clean.length - 1]
  return last ? { date: last.date, weight: Number(last.weight) } : null
}

// --- core metrics ----------------------------------------------------------
// Returns the full Goal Pace view model from the weekly series + settings.
// weeks: chronological weeklyWeightSeries output. weighInCount: total raw
// readings (drives the "building baseline" gate).
export function computePace({ weeks, weighInCount, todayK, settings }) {
  const enoughBaseline = weighInCount >= MIN_WEIGHINS_FOR_BASELINE && weeks.length >= 1
  if (!enoughBaseline) {
    return { phase: 'baseline', weighInCount, currentAvg: weeks.at(-1)?.avg ?? null }
  }

  const currentAvg = weeks.at(-1).avg
  const prev = weeks.length >= 2 ? weeks.at(-2).avg : null
  // Per-step weekly rates (kg/week), most-recent last. Positive = loss.
  const weeklyRates = []
  for (let i = 1; i < weeks.length; i++) weeklyRates.push(weeks[i - 1].avg - weeks[i].avg)

  const hasRate = prev != null
  const weeklyRate = hasRate ? prev - currentAvg : null // +ve = lost weight

  // Trailing 3-week average rate (kg/week): change over up to 3 weeks / span.
  let trailingRate = null
  if (weeks.length >= 2) {
    const span = Math.min(3, weeks.length - 1)
    const earlier = weeks.at(-1 - span).avg
    trailingRate = (earlier - currentAvg) / span
  }

  const band = healthyBandKg(currentAvg)

  // Days / weeks remaining to the target date.
  const daysRemaining = settings?.target_date ? daysBetween(todayK, settings.target_date) : null
  const weeksRemaining = daysRemaining != null ? daysRemaining / 7 : null

  // Projection on the target date using the trailing rate (loss is positive,
  // so we SUBTRACT from current weight as weeks pass).
  let projectedWeight = null
  if (trailingRate != null && weeksRemaining != null) {
    projectedWeight = currentAvg - trailingRate * weeksRemaining
  }

  // Guardrail: too-fast for 2+ consecutive recent weeks.
  const tooFastThreshold = TOO_FAST_FRAC * currentAvg
  const recent2 = weeklyRates.slice(-2)
  const tooFast2wk = recent2.length === 2 && recent2.every((r) => r > tooFastThreshold)

  // Status tone for the headline rate.
  let tone = 'green'
  let reason = 'within'
  if (!hasRate) { tone = 'neutral'; reason = 'noRate' }
  else if (tooFast2wk) { tone = 'amber'; reason = 'tooFast' }
  else if (weeklyRate < band.minKg * 0.5) { tone = 'amber'; reason = 'stalled' }
  else if (weeklyRate >= band.minKg && weeklyRate <= band.maxKg) { tone = 'green'; reason = 'within' }
  else if (weeklyRate > band.maxKg && weeklyRate <= tooFastThreshold) { tone = 'green'; reason = 'within' }
  else if (weeklyRate > tooFastThreshold) { tone = 'amber'; reason = 'fastSingle' }
  else { tone = 'green'; reason = 'within' }

  // Goal feasibility (only when a goal weight is set).
  let goal = null
  const goalKg = settings?.goal_weight_kg != null ? Number(settings.goal_weight_kg) : null
  if (goalKg != null && weeksRemaining != null && weeksRemaining > 0) {
    const requiredRate = (currentAvg - goalKg) / weeksRemaining // kg/wk needed
    const aggressive = requiredRate > band.maxKg
    // Realistic achievable by target date at the top of the healthy band.
    const realisticWeight = currentAvg - band.maxKg * weeksRemaining
    goal = {
      goalKg,
      requiredRate,
      aggressive,
      realisticWeight: Math.max(realisticWeight, goalKg),
      onTrackWeight: projectedWeight,
    }
  }

  // Are we behind the chosen/sane pace? (slower loss than the band minimum)
  const behind = hasRate && weeklyRate < band.minKg

  return {
    phase: 'tracking',
    weighInCount,
    currentAvg,
    weeklyRate,
    trailingRate,
    weeklyRates,
    band,
    daysRemaining,
    weeksRemaining,
    projectedWeight,
    tone,
    reason,
    tooFast2wk,
    behind,
    goal,
  }
}

// Healthy weekly loss band in kg (magnitudes), from current bodyweight.
export function healthyBandKg(currentKg) {
  const w = Number(currentKg) || 0
  return { minKg: BAND_MIN_FRAC * w, maxKg: BAND_MAX_FRAC * w }
}

// Build the "sane target trajectory" for the chart: a straight line from the
// baseline (weight @ date) to the goal weight @ target date — but capped to
// the healthy band so it never draws an unsustainable slope. Sampled at each
// week-end in `weekEndKeys`. Returns map weekEndKey -> target weight (or null
// when there's no goal to draw toward).
export function targetTrajectory({ baselineWeightKg, baselineKey, goalWeightKg, targetDateKey, weekEndKeys, healthyMaxFracOfBaseline }) {
  if (baselineWeightKg == null || !baselineKey) return {}
  const out = {}
  const totalDays = targetDateKey ? daysBetween(baselineKey, targetDateKey) : null

  // Endpoint: goal if set, else a gentle band-mid glide. Cap slope so the
  // line is always "sane" (<= ~1%/wk of baseline).
  let endWeight = goalWeightKg != null ? Number(goalWeightKg) : null
  if (endWeight != null && totalDays && totalDays > 0) {
    const maxDrop = (healthyMaxFracOfBaseline ?? BAND_MAX_FRAC) * baselineWeightKg * (totalDays / 7)
    endWeight = Math.max(endWeight, baselineWeightKg - maxDrop)
  }
  if (endWeight == null || !totalDays || totalDays <= 0) return {}

  const slopePerDay = (endWeight - baselineWeightKg) / totalDays
  for (const k of weekEndKeys) {
    const d = daysBetween(baselineKey, k)
    if (d == null) continue
    out[k] = baselineWeightKg + slopePerDay * d
  }
  return out
}

// --- protein / steps levers ------------------------------------------------
// dailyMap: { 'YYYY-MM-DD': number }. avg over the trailing `window` days that
// actually have data (missing days don't drag the average to zero).
export function trailingAvg(dailyMap, anchorKey, window = 7) {
  let sum = 0
  let n = 0
  for (let i = 0; i < window; i++) {
    const k = addDaysKey(anchorKey, -i)
    if (dailyMap[k] != null) { sum += dailyMap[k]; n += 1 }
  }
  return n ? sum / n : null
}

// Consecutive days (from anchor backward) meeting a daily target. A day with
// no data counts as a miss and breaks the streak.
export function adherenceStreak(dailyMap, anchorKey, target) {
  let streak = 0
  let k = anchorKey
  for (let i = 0; i < 400; i++) {
    const v = dailyMap[k]
    if (v != null && v >= target) { streak += 1; k = addDaysKey(k, -1) } else break
  }
  return streak
}

// Sum meal protein (g) per day -> { date: grams }.
export function dailyProteinMap(meals) {
  const map = {}
  for (const m of meals || []) {
    if (!m?.date) continue
    map[m.date] = (map[m.date] || 0) + (Number(m.protein_g) || 0)
  }
  return map
}

// daily_steps rows -> { day: steps }.
export function dailyStepsMap(rows) {
  const map = {}
  for (const r of rows || []) {
    if (!r?.day) continue
    map[r.day] = Number(r.steps) || 0
  }
  return map
}
